import { Answer, Customizations } from "./survey_question_likert";
import { useNormalizeSurveyQuestionLikertCustomizations } from "./use_normalize_survey_question_likert_customizations";

export const LIKERT_FOLLOW_UP_CHARACTER_LIMIT = 1200;
export const LIKERT_MULTI_SELECT_OTHER_OPTION_FOLLOW_UP_CHARACTER_LIMIT = 75;

export const useSurveyQuestionLikertValidation = (
  customizations: Customizations,
  currentAnswer: Answer | undefined,
  followUpState: { triggeredLowScoreFollowUp: boolean; triggeredHighScoreFollowUp: boolean }
) => {
  const { triggeredLowScoreFollowUp, triggeredHighScoreFollowUp } = followUpState;

  const {
    followUpLowScore,
    followUpLowQuestion,
    followUpHighScore,
    optional,
    followUpLowScoreIsOptional,
    followUpHighScoreIsOptional,
  } = useNormalizeSurveyQuestionLikertCustomizations(customizations);

  const { value, followUpLowResponse, followUpHighResponse } = currentAnswer || {};
  const followUpLowMultiSelectResponse =
    typeof followUpLowResponse !== "string" && followUpLowResponse?.standard !== undefined
      ? followUpLowResponse
      : undefined;

  const hasOpenResponseFollowUp =
    (triggeredLowScoreFollowUp || triggeredHighScoreFollowUp) && !followUpLowQuestion;
  const hasMultiSelectFollowUp =
    (triggeredLowScoreFollowUp || triggeredHighScoreFollowUp) && followUpLowQuestion;
  const followUpLowResponseLength =
    hasOpenResponseFollowUp && followUpLowResponse
      ? (followUpLowResponse as string).length
      : undefined;
  const followUpHighResponseLength =
    hasOpenResponseFollowUp && followUpHighResponse
      ? (followUpHighResponse as string).length
      : undefined;
  const followUpLowResponseWriteInLength =
    hasMultiSelectFollowUp && followUpLowMultiSelectResponse?.writeIn
      ? (followUpLowMultiSelectResponse?.writeIn as string).length
      : undefined;
  const followUpCharacterCount =
    followUpLowResponseLength ??
    followUpHighResponseLength ??
    followUpLowResponseWriteInLength ??
    0;
  const maximumCharacterLimitExceededBy =
    followUpCharacterCount -
    (hasOpenResponseFollowUp
      ? LIKERT_FOLLOW_UP_CHARACTER_LIMIT
      : LIKERT_MULTI_SELECT_OTHER_OPTION_FOLLOW_UP_CHARACTER_LIMIT);

  const deprecatedAnswerIsValid = optional ? true : typeof value !== "undefined";

  const answerIsValid = optional ? true : typeof value !== "undefined";

  const noFollowUpIsAsked =
    (!followUpLowScore && !followUpHighScore) ||
    (!triggeredLowScoreFollowUp && !triggeredHighScoreFollowUp);
  const followUpLowScoreIsValid =
    (triggeredLowScoreFollowUp &&
      followUpLowScore &&
      followUpLowScoreIsOptional &&
      maximumCharacterLimitExceededBy <= 0) ||
    (triggeredLowScoreFollowUp &&
      followUpLowScore &&
      !followUpLowScoreIsOptional &&
      maximumCharacterLimitExceededBy <= 0 &&
      (followUpLowResponseLength ?? 0) > 0) ||
    !followUpLowScore ||
    triggeredHighScoreFollowUp;
  const followUpHighScoreIsValid =
    (triggeredHighScoreFollowUp &&
      followUpHighScore &&
      followUpHighScoreIsOptional &&
      maximumCharacterLimitExceededBy <= 0) ||
    (triggeredHighScoreFollowUp &&
      followUpHighScore &&
      !followUpHighScoreIsOptional &&
      maximumCharacterLimitExceededBy <= 0 &&
      (followUpHighResponseLength ?? 0) > 0) ||
    !followUpHighScore ||
    triggeredLowScoreFollowUp;
  const followUpIsValid =
    noFollowUpIsAsked || (followUpLowScoreIsValid && followUpHighScoreIsValid);

  return {
    deprecatedAnswerIsValid,
    answerIsValid,
    followUpIsValid,
    ableToSubmit: answerIsValid && followUpIsValid,
    maximumCharacterLimitExceededBy,
    followUpCurrentCharacterCount: followUpLowResponseLength ?? followUpHighResponseLength ?? 0,
    maximumCharacterLimitToRespect: hasOpenResponseFollowUp
      ? LIKERT_FOLLOW_UP_CHARACTER_LIMIT
      : hasMultiSelectFollowUp
      ? LIKERT_MULTI_SELECT_OTHER_OPTION_FOLLOW_UP_CHARACTER_LIMIT
      : 0,
  };
};
